import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, ExternalLink, Plus, Check, X, AlertCircle, MapPin, RefreshCw, Image as ImageIcon, Upload, Settings, HelpCircle, MessageSquare, Link as LinkIcon, Trash2, Edit2, ChevronRight, Bell, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUPPORT_EMAIL = 'lawagency360@gmail.com';
const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=400&h=250&fit=crop';

export default function Content({ user, selectedClient }) {
  const [activeSection, setActiveSection] = useState('blogs');
  const [blogs, setBlogs] = useState([]);
  const [pagesOptimized, setPagesOptimized] = useState([]);
  const [metaUpdates, setMetaUpdates] = useState([]);
  const [gmbUpdates, setGmbUpdates] = useState([]);
  const [technicalItems, setTechnicalItems] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [notifying, setNotifying] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedItemForFeedback, setSelectedItemForFeedback] = useState({ type: '', id: '' });
  const [clientFeedback, setClientFeedback] = useState('');
  
  const firmId = selectedClient?.firmId || user?.firm_id;
  const canEdit = user?.role && ['super_admin', 'internal_team'].includes(user.role);
  const isClient = user?.role === 'client';

  useEffect(() => {
    if (firmId) {
      fetchAllContent();
    }
  }, [firmId]);

  const fetchAllContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const baseEndpoint = selectedClient ? `/admin` : '';
      const firmPath = selectedClient ? `/${firmId}` : '';
      
      const [blogsRes, pagesRes, metaRes, gmbRes, techRes, faqsRes] = await Promise.all([
        axios.get(`${API}${baseEndpoint}/blogs${firmPath}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}${baseEndpoint}/pages-optimized${firmPath}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}${baseEndpoint}/meta-updates${firmPath}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}${baseEndpoint}/gmb-updates${firmPath}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}${baseEndpoint}/technical-items${firmPath}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}${baseEndpoint}/faqs${firmPath}`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setBlogs(blogsRes.data || []);
      setPagesOptimized(pagesRes.data || []);
      setMetaUpdates(metaRes.data || []);
      setGmbUpdates(gmbRes.data || []);
      setTechnicalItems(techRes.data || []);
      setFaqs(faqsRes.data || []);
    } catch (err) {
      console.error('Error fetching content:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (type, id, approved) => {
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      let newStatus = approved ? 'Approved' : 'Rejected';
      
      switch (type) {
        case 'blog': endpoint = `/blogs/${id}`; break;
        case 'page': endpoint = `/pages-optimized/${id}`; break;
        case 'meta': endpoint = `/meta-updates/${id}`; break;
        case 'gmb': endpoint = `/gmb-updates/${id}`; break;
        default: return;
      }
      
      await axios.patch(`${API}${endpoint}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAllContent();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error updating status');
    }
  };

  // Notify for approval - Team Member/Admin only
  const handleNotifyForApproval = async (type, items) => {
    const pendingItems = items.filter(item => 
      !['Approved', 'Published', 'Implemented', 'Completed', 'implemented'].includes(item.status)
    );
    
    if (pendingItems.length === 0) {
      alert('No items pending approval');
      return;
    }

    setNotifying(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notify-for-approval`, {
        item_type: type,
        item_ids: pendingItems.map(item => item.id),
        firm_id: firmId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Notification sent to client for ${pendingItems.length} ${type}(s) approval`);
      fetchAllContent();
    } catch (err) {
      console.error('Error sending notification:', err);
      alert(err.response?.data?.detail || 'Error sending notification');
    } finally {
      setNotifying(false);
    }
  };

  // Client approve item
  const handleClientApprove = async (type, id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/approve-item`, 
        { item_type: type, item_id: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAllContent();
    } catch (err) {
      console.error('Error approving item:', err);
      alert(err.response?.data?.detail || 'Error approving item');
    }
  };

  // Client request changes
  const handleClientRequestChanges = async () => {
    if (!selectedItemForFeedback.id) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/request-changes`, 
        { item_type: selectedItemForFeedback.type, item_id: selectedItemForFeedback.id, feedback: clientFeedback },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowFeedbackModal(false);
      setSelectedItemForFeedback({ type: '', id: '' });
      setClientFeedback('');
      fetchAllContent();
    } catch (err) {
      console.error('Error requesting changes:', err);
      alert(err.response?.data?.detail || 'Error requesting changes');
    }
  };

  // Render approval buttons for client
  const renderClientApprovalButtons = (type, item) => {
    if (!isClient) return null;
    const status = item.status || item.approval_status;
    const pending = ['Pending Approval', 'pending_approval', 'pending', 'Ready for Review'].includes(status);
    if (!pending) return null;

    return (
      <div className="flex items-center gap-2 mt-3" data-testid={`client-approval-actions-${type}-${item.id}`}>
        <button
          onClick={() => handleClientApprove(type, item.id)}
          className="px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all"
          data-testid={`approve-${type}-${item.id}`}
        >
          Approve
        </button>
        <button
          onClick={() => {
            setSelectedItemForFeedback({ type, id: item.id });
            setShowFeedbackModal(true);
          }}
          className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-violet-600 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all"
          data-testid={`suggest-changes-${type}-${item.id}`}
        >
          Suggest Changes
        </button>
      </div>
    );
  };

  // Render status with feedback for Changes Requested
  const renderStatusWithFeedback = (item) => {
    if (item.status === 'Changes Requested' && item.client_feedback) {
      return (
        <div>
          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${getStatusColor(item.status)}`}>
            {item.status}
          </span>
          <p className="text-xs text-slate-500 mt-1 italic" title={item.client_feedback}>
            "{item.client_feedback.substring(0, 50)}{item.client_feedback.length > 50 ? '...' : ''}"
          </p>
        </div>
      );
    }
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${getStatusColor(item.status)}`}>
        {item.status}
      </span>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Published': case 'Approved': case 'Implemented': case 'Completed': case 'implemented':
        return 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white';
      case 'Ready for Review': case 'In Review': case 'Pending Approval': case 'Uploaded': case 'Pending': case 'remaining': case 'in_updation':
        return 'bg-gradient-to-r from-amber-400 to-orange-500 text-white';
      case 'Draft': 
        return 'bg-slate-200 text-slate-600';
      case 'Rejected': case 'Changes Requested':
        return 'bg-gradient-to-r from-red-500 to-rose-500 text-white';
      default: 
        return 'bg-slate-200 text-slate-600';
    }
  };

  const openAddModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setShowAddModal(true);
  };

  // Form states for modals
  const [newBlog, setNewBlog] = useState({ title: '', target_keyword: '', slug: '', content: '', meta_title: '', meta_description: '', image_url: '', content_link: '' });
  const [newPage, setNewPage] = useState({ page_url: '', description: '', target_keyword: '' });
  const [newMeta, setNewMeta] = useState({ page_url: '', old_title: '', new_title: '', old_meta_description: '', new_meta_description: '' });
  const [newGmb, setNewGmb] = useState({ update_type: 'Post', description: '' });
  const [newTechItem, setNewTechItem] = useState({ title: '', description: '', status: 'remaining' });
  const [newFaq, setNewFaq] = useState({ page_name: '', question: '', answer: '' });
  const [faqComment, setFaqComment] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const baseEndpoint = selectedClient ? `/admin` : '';
      const firmPath = selectedClient ? `/${firmId}` : '';
      
      switch (modalType) {
        case 'blog':
          await axios.post(`${API}${baseEndpoint}/blogs${firmPath}`, newBlog, { headers });
          setNewBlog({ title: '', target_keyword: '', slug: '', content: '', meta_title: '', meta_description: '', image_url: '', content_link: '' });
          break;
        case 'page':
          await axios.post(`${API}${baseEndpoint}/pages-optimized${firmPath}`, newPage, { headers });
          setNewPage({ page_url: '', description: '', target_keyword: '' });
          break;
        case 'meta':
          await axios.post(`${API}${baseEndpoint}/meta-updates${firmPath}`, newMeta, { headers });
          setNewMeta({ page_url: '', old_title: '', new_title: '', old_meta_description: '', new_meta_description: '' });
          break;
        case 'gmb':
          await axios.post(`${API}${baseEndpoint}/gmb-updates${firmPath}`, newGmb, { headers });
          setNewGmb({ update_type: 'Post', description: '' });
          break;
        case 'tech':
          if (editingItem) {
            await axios.patch(`${API}/technical-items/${editingItem.id}`, newTechItem, { headers });
          } else {
            await axios.post(`${API}${baseEndpoint}/technical-items${firmPath}`, newTechItem, { headers });
          }
          setNewTechItem({ title: '', description: '', status: 'remaining' });
          break;
        case 'faq':
          await axios.post(`${API}${baseEndpoint}/faqs${firmPath}`, newFaq, { headers });
          setNewFaq({ page_name: '', question: '', answer: '' });
          break;
      }
      setShowAddModal(false);
      setEditingItem(null);
      fetchAllContent();
    } catch (err) {
      console.error(`Error creating ${modalType}:`, err);
      const errorDetail = err.response?.data?.detail;
      const validationErrors = err.response?.data?.detail;
      
      // Handle validation errors (422)
      if (err.response?.status === 422 && Array.isArray(validationErrors)) {
        const messages = validationErrors.map(e => `${e.loc?.join('.')}: ${e.msg}`).join('\n');
        alert(`Validation error:\n${messages}`);
      } else if (errorDetail) {
        alert(`Error creating ${modalType}: ${errorDetail}`);
      } else {
        alert(`Error creating ${modalType}. Please check all required fields are filled correctly.`);
      }
    }
  };

  const handleDeleteTechItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/technical-items/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAllContent();
    } catch (err) {
      alert('Error deleting item');
    }
  };

  const handleUpdateTechStatus = async (itemId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/technical-items/${itemId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAllContent();
    } catch (err) {
      alert('Error updating status');
    }
  };

  const handleFaqAction = async (faqId, action, comment = '') => {
    try {
      const token = localStorage.getItem('token');
      const updateData = action === 'approve' 
        ? { status: 'Approved' }
        : { status: 'Changes Requested', client_comment: comment };
      
      await axios.patch(`${API}/faqs/${faqId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFaqComment('');
      fetchAllContent();
    } catch (err) {
      alert('Error updating FAQ');
    }
  };

  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/faqs/${faqId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAllContent();
    } catch (err) {
      alert('Error deleting FAQ');
    }
  };

  // Group FAQs by page name
  const groupedFaqs = faqs.reduce((acc, faq) => {
    if (!acc[faq.page_name]) acc[faq.page_name] = [];
    acc[faq.page_name].push(faq);
    return acc;
  }, {});

  // Group technical items by status
  const techByStatus = {
    implemented: technicalItems.filter(i => i.status === 'implemented'),
    remaining: technicalItems.filter(i => i.status === 'remaining'),
    in_updation: technicalItems.filter(i => i.status === 'in_updation')
  };

  return (
    <div className="space-y-8 animate-fadeInUp" data-testid="content-page">
      {/* Header */}
      <div className="flex items-center justify-between opacity-0 animate-fadeInUp" style={{animationDelay: '0s', animationFillMode: 'forwards'}}>
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>
            Content & Assets
          </h1>
          <p className="text-slate-500 text-lg">Track all content creation and optimization work</p>
        </div>
        <button 
          onClick={fetchAllContent} 
          className="flex items-center gap-2 px-5 py-2.5 bg-white/70 backdrop-blur-sm border border-slate-200/50 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300"
        >
          <RefreshCw className="w-4 h-4 text-slate-600" />
          <span className="text-slate-700 font-medium">Refresh</span>
        </button>
      </div>

      {/* Implementation Notice */}
      <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200/50 rounded-2xl p-5 flex items-start gap-4 opacity-0 animate-fadeInUp soft-shadow" style={{animationDelay: '0.1s', animationFillMode: 'forwards'}} data-testid="support-notice">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-slate-800 font-semibold">Need updates implemented on your website?</p>
          <p className="text-slate-600 text-sm mt-1">
            If you would like us to implement all updates directly on your website, please provide access to{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-violet-600 font-semibold hover:underline">{SUPPORT_EMAIL}</a>
          </p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 opacity-0 animate-fadeInUp" style={{animationDelay: '0.2s', animationFillMode: 'forwards'}}>
        {[
          { label: 'Blogs', value: blogs.length, sub: `${blogs.filter(b => b.status === 'Published').length} published`, gradient: 'from-violet-500 to-indigo-600' },
          { label: 'Pages', value: pagesOptimized.length, sub: `${pagesOptimized.filter(p => p.status === 'Implemented').length} implemented`, gradient: 'from-blue-500 to-cyan-500' },
          { label: 'Metadata', value: metaUpdates.length, sub: `${metaUpdates.filter(m => m.status === 'Approved').length} approved`, gradient: 'from-emerald-500 to-teal-500' },
          { label: 'GMB', value: gmbUpdates.length, sub: `${gmbUpdates.filter(g => g.status === 'Completed').length} completed`, gradient: 'from-pink-500 to-rose-500' },
          { label: 'Technical', value: technicalItems.length, sub: `${techByStatus.implemented.length} done`, gradient: 'from-amber-500 to-orange-500' },
          { label: 'FAQs', value: faqs.length, sub: `${faqs.filter(f => f.status === 'Approved').length} approved`, gradient: 'from-indigo-500 to-purple-500' }
        ].map((stat, idx) => (
          <div key={stat.label} className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-5 soft-shadow hover-lift">
            <div className="text-xs text-slate-500 font-medium mb-1">{stat.label}</div>
            <div className={`text-3xl font-bold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`} style={{fontFamily: 'Outfit'}}>{stat.value}</div>
            <div className="text-xs text-slate-400 mt-1">{stat.sub}</div>
          </div>
        ))}
      </div>

      {/* Section Tabs */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 overflow-hidden soft-shadow-lg opacity-0 animate-fadeInUp" style={{animationDelay: '0.3s', animationFillMode: 'forwards'}}>
        <div className="border-b border-slate-200/50 bg-gradient-to-r from-slate-50/50 to-white/50 overflow-x-auto">
          <div className="flex">
            {['blogs', 'pages', 'metadata', 'gmb', 'technical', 'faqs'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveSection(tab)}
                className={`px-5 py-4 font-medium transition-all duration-300 whitespace-nowrap ${
                  activeSection === tab
                    ? 'text-violet-600 border-b-2 border-violet-600 bg-white/50'
                    : 'text-slate-500 hover:text-slate-800 hover:bg-white/30'
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab === 'blogs' && 'Blogs'}
                {tab === 'pages' && 'Pages Optimized'}
                {tab === 'metadata' && 'Metadata'}
                {tab === 'gmb' && 'GMB Updates'}
                {tab === 'technical' && 'Technical'}
                {tab === 'faqs' && 'FAQs'}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto"></div>
          </div>
        ) : (
          <>
            {/* Blogs Section - Card Layout */}
            {activeSection === 'blogs' && (
              <div data-testid="blogs-section">
                {canEdit && (
                  <div className="p-5 border-b border-slate-200/50 bg-gradient-to-r from-slate-50/30 to-white/30 flex items-center gap-3">
                    <button
                      onClick={() => handleNotifyForApproval('blog', blogs)}
                      disabled={notifying}
                      className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-all disabled:opacity-50"
                      data-testid="notify-blogs-approval-btn"
                    >
                      {notifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                      Notify for Approval
                    </button>
                    <button onClick={() => openAddModal('blog')} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300 font-medium" data-testid="add-blog-btn">
                      <Plus className="w-4 h-4" /> Add Blog
                    </button>
                  </div>
                )}
                
                {blogs.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">No blogs yet</div>
                ) : (
                  <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {blogs.map((blog) => (
                      <div key={blog.id} className="bg-white rounded-2xl border border-slate-200/50 overflow-hidden hover-lift soft-shadow group" data-testid={`blog-card-${blog.id}`}>
                        {/* Blog Image */}
                        <div className="relative h-48 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
                          <img 
                            src={blog.image_url || PLACEHOLDER_IMAGE} 
                            alt={blog.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
                          />
                          <div className="absolute top-3 right-3">
                            <span className={`px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg ${getStatusColor(blog.status)}`}>
                              {blog.status}
                            </span>
                          </div>
                        </div>
                        
                        {/* Blog Content */}
                        <div className="p-5">
                          <h3 className="font-bold text-slate-900 text-lg mb-2 line-clamp-2" style={{fontFamily: 'Outfit'}}>{blog.title}</h3>
                          
                          <div className="flex items-center gap-2 mb-3">
                            <span className="px-2.5 py-1 bg-violet-100 text-violet-700 rounded-lg text-xs font-medium">
                              {blog.target_keyword}
                            </span>
                          </div>
                          
                          <div className="text-sm text-slate-500 mb-4">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-slate-600">Slug:</span>
                              <span className="text-violet-600">{blog.slug}</span>
                            </div>
                            {/* Blog Content Link - NEW */}
                            {blog.content_link && (
                              <div className="flex items-center gap-2 mt-2">
                                <LinkIcon className="w-4 h-4 text-violet-500" />
                                <a href={blog.content_link} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline truncate">
                                  View Content
                                </a>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                            <span className="text-xs text-slate-400">
                              {blog.created_at ? new Date(blog.created_at).toLocaleDateString() : '-'}
                            </span>
                            {blog.status === 'Ready for Review' && !isClient && (
                              <div className="flex items-center gap-2">
                                <button onClick={() => handleApprove('blog', blog.id, true)} className="p-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:shadow-lg transition-all" title="Approve" data-testid={`approve-blog-${blog.id}`}>
                                  <Check className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleApprove('blog', blog.id, false)} className="p-2 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-lg hover:shadow-lg transition-all" title="Reject">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                          {renderClientApprovalButtons('blog', blog)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pages Optimized Section */}
            {activeSection === 'pages' && (
              <div data-testid="pages-section">
                {canEdit && (
                  <div className="p-5 border-b border-slate-200/50">
                    <button onClick={() => openAddModal('page')} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300 font-medium" data-testid="add-page-btn">
                      <Plus className="w-4 h-4" /> Add Page Optimization
                    </button>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full modern-table">
                    <thead>
                      <tr>
                        <th className="px-6 py-4 text-left">Page URL</th>
                        <th className="px-6 py-4 text-left">Description</th>
                        <th className="px-6 py-4 text-left">Target Keyword</th>
                        <th className="px-6 py-4 text-left">Status</th>
                        <th className="px-6 py-4 text-left">Date</th>
                        <th className="px-6 py-4 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {pagesOptimized.map((page) => (
                        <tr key={page.id}>
                          <td className="px-6 py-4">
                            <a href={page.page_url} className="text-violet-600 hover:underline flex items-center gap-1 font-medium">
                              {page.page_url} <ExternalLink className="w-3 h-3" />
                            </a>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600 max-w-xs">{page.description}</td>
                          <td className="px-6 py-4">
                            <span className="px-2.5 py-1 bg-violet-100 text-violet-700 rounded-lg text-xs font-medium">{page.target_keyword}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${getStatusColor(page.status)}`}>
                              {page.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-500">
                            {page.created_at ? new Date(page.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-6 py-4">
                            {page.status === 'In Review' && (
                              <div className="flex items-center gap-2">
                                <button onClick={() => handleApprove('page', page.id, true)} className="p-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:shadow-lg transition-all" title="Approve">
                                  <Check className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleApprove('page', page.id, false)} className="p-2 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-lg hover:shadow-lg transition-all" title="Reject">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {pagesOptimized.length === 0 && (
                        <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500">No page optimizations yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Metadata Changes Section */}
            {activeSection === 'metadata' && (
              <div data-testid="metadata-section">
                {canEdit && (
                  <div className="p-5 border-b border-slate-200/50">
                    <button onClick={() => openAddModal('meta')} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300 font-medium" data-testid="add-meta-btn">
                      <Plus className="w-4 h-4" /> Add Meta Update
                    </button>
                  </div>
                )}
                <div className="p-6 space-y-5">
                  {metaUpdates.map((change) => (
                    <div key={change.id} className="bg-gradient-to-r from-slate-50/50 to-white rounded-2xl border border-slate-200/50 p-6 hover-lift">
                      <div className="flex items-start justify-between mb-5">
                        <a href={change.page_url} className="text-violet-600 hover:underline flex items-center gap-2 font-semibold text-lg">
                          {change.page_url} <ExternalLink className="w-4 h-4" />
                        </a>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${getStatusColor(change.status)}`}>
                            {change.status}
                          </span>
                          {change.status === 'Pending Approval' && (
                            <div className="flex items-center gap-2">
                              <button onClick={() => handleApprove('meta', change.id, true)} className="p-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:shadow-lg transition-all" title="Approve">
                                <Check className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleApprove('meta', change.id, false)} className="p-2 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-lg hover:shadow-lg transition-all" title="Reject">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="space-y-3">
                          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Old Title Tag</div>
                          <div className="text-sm text-slate-600 bg-slate-100 p-4 rounded-xl">{change.old_title}</div>
                          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Old Meta Description</div>
                          <div className="text-sm text-slate-600 bg-slate-100 p-4 rounded-xl">{change.old_meta_description}</div>
                        </div>
                        <div className="space-y-3">
                          <div className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">New Title Tag</div>
                          <div className="text-sm text-slate-900 bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-xl font-medium border border-emerald-200/50">{change.new_title}</div>
                          <div className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">New Meta Description</div>
                          <div className="text-sm text-slate-900 bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-xl font-medium border border-emerald-200/50">{change.new_meta_description}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {metaUpdates.length === 0 && (
                    <div className="text-center py-12 text-slate-500">No metadata changes yet</div>
                  )}
                </div>
              </div>
            )}

            {/* GMB Updates Section */}
            {activeSection === 'gmb' && (
              <div data-testid="gmb-section">
                {canEdit && (
                  <div className="p-5 border-b border-slate-200/50">
                    <button onClick={() => openAddModal('gmb')} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300 font-medium" data-testid="add-gmb-btn">
                      <Plus className="w-4 h-4" /> Add GMB Update
                    </button>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full modern-table">
                    <thead>
                      <tr>
                        <th className="px-6 py-4 text-left">Update Type</th>
                        <th className="px-6 py-4 text-left">Description</th>
                        <th className="px-6 py-4 text-left">Date</th>
                        <th className="px-6 py-4 text-left">Status</th>
                        <th className="px-6 py-4 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {gmbUpdates.map((update) => (
                        <tr key={update.id}>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                                <MapPin className="w-5 h-5 text-white" />
                              </div>
                              <span className="font-semibold text-slate-900">{update.update_type}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600">{update.description}</td>
                          <td className="px-6 py-4 text-sm text-slate-500">
                            {update.created_at ? new Date(update.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${getStatusColor(update.status)}`}>
                              {update.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {update.status === 'Uploaded' && (
                              <button onClick={() => handleApprove('gmb', update.id, true)} className="p-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:shadow-lg transition-all" title="Approve">
                                <Check className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {gmbUpdates.length === 0 && (
                        <tr><td colSpan={5} className="px-6 py-12 text-center text-slate-500">No GMB updates yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Technical Implementations Section - NEW */}
            {activeSection === 'technical' && (
              <div data-testid="technical-section" className="p-6">
                {canEdit && (
                  <div className="mb-6">
                    <button onClick={() => openAddModal('tech')} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300 font-medium">
                      <Plus className="w-4 h-4" /> Add Technical Item
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Implemented Section */}
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200/50 p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="font-bold text-slate-900">Implemented</h3>
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">{techByStatus.implemented.length}</span>
                    </div>
                    <div className="space-y-3">
                      {techByStatus.implemented.map(item => (
                        <TechnicalItemCard 
                          key={item.id} 
                          item={item} 
                          canEdit={canEdit} 
                          onEdit={() => { setNewTechItem(item); openAddModal('tech', item); }}
                          onDelete={() => handleDeleteTechItem(item.id)}
                          onStatusChange={handleUpdateTechStatus}
                        />
                      ))}
                      {techByStatus.implemented.length === 0 && (
                        <p className="text-sm text-slate-400 text-center py-4">No items yet</p>
                      )}
                    </div>
                  </div>

                  {/* Remaining Section */}
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200/50 p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                        <AlertCircle className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="font-bold text-slate-900">Remaining</h3>
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">{techByStatus.remaining.length}</span>
                    </div>
                    <div className="space-y-3">
                      {techByStatus.remaining.map(item => (
                        <TechnicalItemCard 
                          key={item.id} 
                          item={item} 
                          canEdit={canEdit}
                          onEdit={() => { setNewTechItem(item); openAddModal('tech', item); }}
                          onDelete={() => handleDeleteTechItem(item.id)}
                          onStatusChange={handleUpdateTechStatus}
                        />
                      ))}
                      {techByStatus.remaining.length === 0 && (
                        <p className="text-sm text-slate-400 text-center py-4">No items yet</p>
                      )}
                    </div>
                  </div>

                  {/* In Updation Section */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200/50 p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                        <RefreshCw className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="font-bold text-slate-900">In Updation</h3>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">{techByStatus.in_updation.length}</span>
                    </div>
                    <div className="space-y-3">
                      {techByStatus.in_updation.map(item => (
                        <TechnicalItemCard 
                          key={item.id} 
                          item={item} 
                          canEdit={canEdit}
                          onEdit={() => { setNewTechItem(item); openAddModal('tech', item); }}
                          onDelete={() => handleDeleteTechItem(item.id)}
                          onStatusChange={handleUpdateTechStatus}
                        />
                      ))}
                      {techByStatus.in_updation.length === 0 && (
                        <p className="text-sm text-slate-400 text-center py-4">No items yet</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* FAQs Section - NEW */}
            {activeSection === 'faqs' && (
              <div data-testid="faqs-section" className="p-6">
                {canEdit && (
                  <div className="mb-6">
                    <button onClick={() => openAddModal('faq')} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300 font-medium">
                      <Plus className="w-4 h-4" /> Add FAQ
                    </button>
                  </div>
                )}

                {Object.keys(groupedFaqs).length === 0 ? (
                  <div className="text-center py-12 text-slate-500">No FAQs yet</div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(groupedFaqs).map(([pageName, pageFaqs]) => (
                      <div key={pageName} className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 overflow-hidden">
                        <div className="p-5 bg-gradient-to-r from-slate-50/50 to-white/50 border-b border-slate-200/50">
                          <h3 className="font-bold text-slate-900 flex items-center gap-2">
                            <FileText className="w-5 h-5 text-violet-600" />
                            {pageName}
                          </h3>
                        </div>
                        <div className="p-5 space-y-4">
                          {pageFaqs.map(faq => (
                            <FAQCard 
                              key={faq.id}
                              faq={faq}
                              canEdit={canEdit}
                              isClient={isClient}
                              onApprove={() => handleFaqAction(faq.id, 'approve')}
                              onSuggestChanges={(comment) => handleFaqAction(faq.id, 'changes', comment)}
                              onDelete={() => handleDeleteFaq(faq.id)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (canEdit || modalType === 'tech') && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto soft-shadow-lg animate-fadeInUp">
            <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Outfit'}}>
              {modalType === 'blog' && 'Add Blog for Review'}
              {modalType === 'page' && 'Add Page Optimization'}
              {modalType === 'meta' && 'Add Metadata Change'}
              {modalType === 'gmb' && 'Add GMB Update'}
              {modalType === 'tech' && (editingItem ? 'Edit Technical Item' : 'Add Technical Item')}
              {modalType === 'faq' && 'Add FAQ'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              {modalType === 'blog' && (
                <>
                  {/* Blog Image URL */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Blog Image URL</label>
                    <input 
                      type="text" 
                      value={newBlog.image_url} 
                      onChange={(e) => setNewBlog({...newBlog, image_url: e.target.value})} 
                      placeholder="https://example.com/image.jpg" 
                      className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" 
                    />
                  </div>
                  {/* Blog Content Link - NEW */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Blog Content Link *</label>
                    <input 
                      type="url" 
                      value={newBlog.content_link} 
                      onChange={(e) => setNewBlog({...newBlog, content_link: e.target.value})} 
                      required
                      placeholder="https://docs.google.com/document/..." 
                      className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" 
                    />
                    <p className="text-xs text-slate-400 mt-1">Link to the full blog content (Google Doc, Notion, etc.)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Blog Title *</label>
                    <input type="text" value={newBlog.title} onChange={(e) => setNewBlog({...newBlog, title: e.target.value})} required className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Target Keyword *</label>
                      <input type="text" value={newBlog.target_keyword} onChange={(e) => setNewBlog({...newBlog, target_keyword: e.target.value})} required className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Slug *</label>
                      <input type="text" value={newBlog.slug} onChange={(e) => setNewBlog({...newBlog, slug: e.target.value})} required placeholder="/blog/my-article" className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Blog Summary *</label>
                    <textarea value={newBlog.content} onChange={(e) => setNewBlog({...newBlog, content: e.target.value})} required rows={3} placeholder="Brief summary of the blog content..." className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Meta Title *</label>
                    <input type="text" value={newBlog.meta_title} onChange={(e) => setNewBlog({...newBlog, meta_title: e.target.value})} required className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Meta Description *</label>
                    <textarea value={newBlog.meta_description} onChange={(e) => setNewBlog({...newBlog, meta_description: e.target.value})} required rows={2} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                  </div>
                </>
              )}
              
              {modalType === 'page' && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Page URL *</label>
                    <input type="text" value={newPage.page_url} onChange={(e) => setNewPage({...newPage, page_url: e.target.value})} required placeholder="/practice-areas/personal-injury" className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Description of Optimization *</label>
                    <textarea value={newPage.description} onChange={(e) => setNewPage({...newPage, description: e.target.value})} required rows={4} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Target Keyword *</label>
                    <input type="text" value={newPage.target_keyword} onChange={(e) => setNewPage({...newPage, target_keyword: e.target.value})} required className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                  </div>
                </>
              )}
              
              {modalType === 'meta' && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Page URL *</label>
                    <input type="text" value={newMeta.page_url} onChange={(e) => setNewMeta({...newMeta, page_url: e.target.value})} required className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Old Title *</label>
                      <input type="text" value={newMeta.old_title} onChange={(e) => setNewMeta({...newMeta, old_title: e.target.value})} required className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">New Title *</label>
                      <input type="text" value={newMeta.new_title} onChange={(e) => setNewMeta({...newMeta, new_title: e.target.value})} required className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Old Meta Description *</label>
                      <textarea value={newMeta.old_meta_description} onChange={(e) => setNewMeta({...newMeta, old_meta_description: e.target.value})} required rows={3} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">New Meta Description *</label>
                      <textarea value={newMeta.new_meta_description} onChange={(e) => setNewMeta({...newMeta, new_meta_description: e.target.value})} required rows={3} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                    </div>
                  </div>
                </>
              )}
              
              {modalType === 'gmb' && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Update Type *</label>
                    <select value={newGmb.update_type} onChange={(e) => setNewGmb({...newGmb, update_type: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all">
                      <option value="Post">Post</option>
                      <option value="Photo Upload">Photo Upload</option>
                      <option value="Service Added">Service Added</option>
                      <option value="Review Response">Review Response</option>
                      <option value="Info Update">Info Update</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Description *</label>
                    <textarea value={newGmb.description} onChange={(e) => setNewGmb({...newGmb, description: e.target.value})} required rows={4} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                  </div>
                </>
              )}

              {modalType === 'tech' && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Title *</label>
                    <input type="text" value={newTechItem.title} onChange={(e) => setNewTechItem({...newTechItem, title: e.target.value})} required placeholder="e.g., Robots.txt file, Sitemap.xml" className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
                    <textarea value={newTechItem.description || ''} onChange={(e) => setNewTechItem({...newTechItem, description: e.target.value})} rows={3} placeholder="Optional description..." className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Status *</label>
                    <select value={newTechItem.status} onChange={(e) => setNewTechItem({...newTechItem, status: e.target.value})} className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all">
                      <option value="remaining">Remaining to be Implemented</option>
                      <option value="in_updation">In Updation</option>
                      <option value="implemented">Implemented</option>
                    </select>
                  </div>
                </>
              )}

              {modalType === 'faq' && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Page Name *</label>
                    <input type="text" value={newFaq.page_name} onChange={(e) => setNewFaq({...newFaq, page_name: e.target.value})} required placeholder="e.g., Home Page, About Us" className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Question *</label>
                    <input type="text" value={newFaq.question} onChange={(e) => setNewFaq({...newFaq, question: e.target.value})} required className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Answer *</label>
                    <textarea value={newFaq.answer} onChange={(e) => setNewFaq({...newFaq, answer: e.target.value})} required rows={4} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all" />
                  </div>
                </>
              )}

              <div className="flex justify-end gap-3 pt-6">
                <button type="button" onClick={() => { setShowAddModal(false); setEditingItem(null); }} className="px-6 py-3 border border-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-all">Cancel</button>
                <button type="submit" className="px-6 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-violet-500/30 transition-all">{editingItem ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Client Feedback Modal for Suggesting Changes */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 soft-shadow-lg animate-fadeInUp">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
                Suggest Changes
              </h3>
            </div>
            
            <p className="text-sm text-slate-600 mb-4">
              Please provide your feedback for this {selectedItemForFeedback.type}:
            </p>
            
            <textarea
              value={clientFeedback}
              onChange={(e) => setClientFeedback(e.target.value)}
              rows={4}
              placeholder="e.g., Please revise the title to be more descriptive..."
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="content-feedback-input"
            />
            
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowFeedbackModal(false);
                  setSelectedItemForFeedback({ type: '', id: '' });
                  setClientFeedback('');
                }}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleClientRequestChanges}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-violet-600 text-white rounded-xl font-medium hover:shadow-lg"
                data-testid="content-submit-feedback-btn"
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Technical Item Card Component
function TechnicalItemCard({ item, canEdit, onEdit, onDelete, onStatusChange }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-slate-200/50 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="font-semibold text-slate-900 text-sm">{item.title}</h4>
          {item.description && (
            <p className="text-xs text-slate-500 mt-1">{item.description}</p>
          )}
          <p className="text-xs text-slate-400 mt-2">
            {item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-1 ml-2">
            <button onClick={onEdit} className="p-1.5 text-slate-400 hover:text-violet-600 transition-colors">
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={onDelete} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      {canEdit && (
        <div className="flex gap-1 mt-3 pt-3 border-t border-slate-100">
          {item.status !== 'implemented' && (
            <button onClick={() => onStatusChange(item.id, 'implemented')} className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 transition-colors">
              Done
            </button>
          )}
          {item.status !== 'in_updation' && (
            <button onClick={() => onStatusChange(item.id, 'in_updation')} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors">
              In Progress
            </button>
          )}
          {item.status !== 'remaining' && (
            <button onClick={() => onStatusChange(item.id, 'remaining')} className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 transition-colors">
              Pending
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// FAQ Card Component
function FAQCard({ faq, canEdit, isClient, onApprove, onSuggestChanges, onDelete }) {
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [comment, setComment] = useState('');

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white';
      case 'Changes Requested': return 'bg-gradient-to-r from-red-500 to-rose-500 text-white';
      default: return 'bg-gradient-to-r from-amber-400 to-orange-500 text-white';
    }
  };

  return (
    <div className="bg-gradient-to-r from-slate-50/50 to-white rounded-xl p-5 border border-slate-200/50">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-violet-500" />
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(faq.status)}`}>
            {faq.status}
          </span>
        </div>
        {canEdit && (
          <button onClick={onDelete} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      
      <h4 className="font-semibold text-slate-900 mb-2">{faq.question}</h4>
      <p className="text-sm text-slate-600 mb-3">{faq.answer}</p>
      
      {faq.client_comment && (
        <div className="bg-amber-50 border border-amber-200/50 rounded-lg p-3 mb-3">
          <div className="flex items-center gap-2 text-xs text-amber-700 font-semibold mb-1">
            <MessageSquare className="w-3 h-3" /> Client Comment:
          </div>
          <p className="text-sm text-amber-800">{faq.client_comment}</p>
        </div>
      )}
      
      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <span className="text-xs text-slate-400">
          {faq.created_at ? new Date(faq.created_at).toLocaleDateString() : '-'}
        </span>
        
        {/* Client Actions */}
        {isClient && faq.status === 'Pending' && (
          <div className="flex items-center gap-2">
            <button onClick={onApprove} className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg text-xs font-medium hover:shadow-lg transition-all">
              <Check className="w-3 h-3" /> Approve
            </button>
            <button onClick={() => setShowCommentInput(!showCommentInput)} className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-lg text-xs font-medium hover:shadow-lg transition-all">
              <MessageSquare className="w-3 h-3" /> Suggest Changes
            </button>
          </div>
        )}
      </div>
      
      {/* Comment Input */}
      {showCommentInput && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Enter your suggested changes..."
            rows={2}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setShowCommentInput(false)} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
              Cancel
            </button>
            <button onClick={() => { onSuggestChanges(comment); setShowCommentInput(false); setComment(''); }} className="px-3 py-1.5 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700">
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
